import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { sacI18n } from './sacI18n';
import { getSacTranscripts } from '../../services/marketingReportService';
import {
  getProtocolsGroupedByBusinessLine,
  getProtocolSteps,
} from '../../services/protocolService';
import loaderSpinnerAnimation from 'assets/images/loader-spinner.json';
import './sacTranscriptsModal.scss';

const DEFAULT_FILTERS = {
  dateFrom: '',
  dateTo: '',
  feeling: '',
  maxRows: 120,
  excludeOmittedAudio: true,
};

const getFeelingOptions = (t) => [
  { label: t('sac_modal_feelingAll'), value: '' },
  { label: t('sac_modal_feelingPositive'), value: 'positivo' },
  { label: t('sac_modal_feelingNeutral'), value: 'neutral' },
  { label: t('sac_modal_feelingNegative'), value: 'negativo' },
];

const LOTTIE_SCRIPT_SRC =
  'https://unpkg.com/lottie-web@5.12.2/build/player/lottie.min.js';
let lottieScriptPromise;

const loadLottiePlayer = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.lottie) return Promise.resolve(window.lottie);
  if (!lottieScriptPromise) {
    lottieScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = LOTTIE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve(window.lottie || null);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }
  return lottieScriptPromise;
};

const clampRows = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_FILTERS.maxRows;
  return Math.min(500, Math.max(1, Math.floor(parsed)));
};

const toDisplayDateTime = (value) => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const toDisplayDate = (value) => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
};

const normalizeResponse = (payload) => {
  const source =
    payload?.rowCount !== undefined || Array.isArray(payload?.data)
      ? payload
      : payload?.data || {};
  const data = Array.isArray(source?.data) ? source.data : [];
  const rowCount = Number(source?.rowCount);

  const normalizedRows = data.map((row, index) => ({
    ...row,
    __rowKey:
      row?.id ??
      row?.s3Path ??
      row?.createdAt ??
      row?.audioDate ??
      `sac-row-${index}`,
  }));

  return {
    data: normalizedRows,
    rowCount: Number.isFinite(rowCount) ? rowCount : normalizedRows.length,
  };
};

const normalizeProtocolGroups = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const mapStepForPayload = (step, index, t) => {
  const stepName = (
    step?.stepName ||
    step?.name ||
    step?.stepDescription ||
    step?.description ||
    step?.descripcion ||
    step?.detalle ||
    t('sac_modal_stepFallback', { index: index + 1 })
  )
    .toString()
    .trim();

  const guidanceSource =
    step?.guidance ??
    step?.stepGuidance ??
    step?.notes ??
    step?.note ??
    step?.guideline ??
    null;

  const guidance =
    typeof guidanceSource === 'string' && guidanceSource.trim()
      ? guidanceSource.trim()
      : null;

  return { stepName, guidance };
};

const SacTranscriptsFilterModal = ({
  Modal,
  Input,
  ComboBox,
  Button,
  Table,
  show,
  onClose,
  onAnalyze,
  initialFilters,
}) => {
  const { language } = useLanguage();
  const sacLanguage = normalizeSupportedLanguage(language);

  if (sacI18n.language !== sacLanguage) {
    sacI18n.changeLanguage(sacLanguage);
  }

  const { t } = useTranslation(undefined, { i18n: sacI18n });

  const feelingOptions = useMemo(() => getFeelingOptions(t), [sacLanguage]);

  const getFeelingOption = (value) =>
    feelingOptions.find((item) => item.value === value) || feelingOptions[0];

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [analyzeError, setAnalyzeError] = useState('');

  const [protocolOptions, setProtocolOptions] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [protocolSteps, setProtocolSteps] = useState([]);
  const [loadingProtocols, setLoadingProtocols] = useState(false);
  const [loadingProtocolSteps, setLoadingProtocolSteps] = useState(false);
  const [protocolError, setProtocolError] = useState('');
  const [analyzeHasLottie, setAnalyzeHasLottie] = useState(false);

  const requestControllerRef = useRef(null);
  const headerCheckboxRef = useRef(null);
  const analyzeLoaderRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setFilters({
      dateFrom: initialFilters?.dateFrom || '',
      dateTo: initialFilters?.dateTo || '',
      feeling: initialFilters?.feeling || '',
      maxRows: clampRows(initialFilters?.maxRows ?? DEFAULT_FILTERS.maxRows),
      excludeOmittedAudio: true,
    });
    setRows([]);
    setRowCount(0);
    setSelectedIds([]);
    setSearchError('');
    setAnalyzeError('');
    setProtocolError('');
    setSelectedProtocol(null);
    setProtocolSteps([]);

    let ignore = false;

    const loadProtocols = async () => {
      setLoadingProtocols(true);
      try {
        const response = await getProtocolsGroupedByBusinessLine();
        if (ignore) return;
        const groups = normalizeProtocolGroups(response);
        const flattened = groups.flatMap((group) =>
          (group?.protocols || []).map((protocol) => ({
            label:
              protocol?.protocolName || t('sac_modal_protocolFallback', { id: protocol?.protocolId }),
            value: protocol?.protocolId,
            businessLineName: group?.businessLineName || '',
          }))
        );
        setProtocolOptions(flattened);
      } catch (error) {
        if (ignore) return;
        setProtocolOptions([]);
        setProtocolError(t('sac_modal_err_protocols'));
      } finally {
        if (!ignore) {
          setLoadingProtocols(false);
        }
      }
    };

    loadProtocols();

    return () => {
      ignore = true;
    };
  }, [show, initialFilters]);

  useEffect(() => {
    return () => {
      requestControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!loadingAnalyze) return undefined;

    let animation = null;
    let cancelled = false;
    const loaderEl = analyzeLoaderRef.current;
    setAnalyzeHasLottie(false);

    loadLottiePlayer()
      .then((lottie) => {
        if (cancelled || !lottie || !loaderEl) return;
        animation = lottie.loadAnimation({
          container: loaderEl,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: loaderSpinnerAnimation,
        });
        setAnalyzeHasLottie(true);
      })
      .catch(() => {
        setAnalyzeHasLottie(false);
      });

    return () => {
      cancelled = true;
      if (animation) animation.destroy();
      if (loaderEl?.classList?.contains('marketing-sac__spinner')) {
        loaderEl.innerHTML = '';
      }
    };
  }, [loadingAnalyze]);

  useEffect(() => {
    if (!show) return;
    if (!selectedProtocol?.value) {
      setProtocolSteps([]);
      return;
    }

    let ignore = false;
    const loadSteps = async () => {
      setLoadingProtocolSteps(true);
      setProtocolError('');
      try {
        const response = await getProtocolSteps(selectedProtocol.value);
        if (ignore) return;
        setProtocolSteps(Array.isArray(response) ? response : []);
      } catch (error) {
        if (ignore) return;
        setProtocolSteps([]);
        setProtocolError(t('sac_modal_err_steps'));
      } finally {
        if (!ignore) {
          setLoadingProtocolSteps(false);
        }
      }
    };

    loadSteps();

    return () => {
      ignore = true;
    };
  }, [show, selectedProtocol]);

  const protocolStepsPayload = useMemo(
    () =>
      protocolSteps
        .map((step, index) => mapStepForPayload(step, index, t))
        .filter((step) => step.stepName),
    [protocolSteps, sacLanguage]
  );

  const dateRangeError = useMemo(() => {
    if (!filters.dateFrom || !filters.dateTo) return '';
    if (filters.dateFrom > filters.dateTo) {
      return t('sac_modal_err_dateRange');
    }
    return '';
  }, [filters.dateFrom, filters.dateTo, sacLanguage]);

  const rowKeys = useMemo(
    () => rows.map((row) => row.__rowKey).filter(Boolean),
    [rows]
  );
  const allSelected =
    rowKeys.length > 0 && rowKeys.every((id) => selectedIds.includes(id));
  const someSelected =
    rowKeys.some((id) => selectedIds.includes(id)) && !allSelected;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => rowKeys.includes(id)));
  }, [rowKeys]);

  const handleChange = (key) => (event) => {
    const nextValue = event?.target ? event.target.value : event;
    setFilters((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleFeelingChange = (option) => {
    setFilters((prev) => ({ ...prev, feeling: option?.value || '' }));
  };

  const handleProtocolChange = (option) => {
    setSelectedProtocol(option || null);
    setProtocolSteps([]);
    setAnalyzeError('');
  };

  const toggleRow = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey)
        ? prev.filter((value) => value !== rowKey)
        : [...prev, rowKey]
    );
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (!rowKeys.length) return prev;
      if (allSelected) return prev.filter((id) => !rowKeys.includes(id));
      const next = new Set(prev);
      rowKeys.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSearch = async () => {
    if (dateRangeError) return;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;

    setLoadingSearch(true);
    setSearchError('');
    setAnalyzeError('');

    const payload = {
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      feeling: filters.feeling || undefined,
      maxRows: clampRows(filters.maxRows),
      excludeOmittedAudio: true,
    };

    try {
      const response = await getSacTranscripts(payload, controller.signal);
      const normalized = normalizeResponse(response);
      setRows(normalized.data);
      setRowCount(normalized.rowCount);
      setSelectedIds([]);
      setFilters((prev) => ({ ...prev, maxRows: payload.maxRows }));
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      setRows([]);
      setRowCount(0);
      setSearchError(
        error?.customMessage || t('sac_modal_err_search')
      );
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedIds.length || loadingAnalyze) return;
    if (!selectedProtocol?.value) {
      setAnalyzeError(t('sac_modal_err_noProtocol'));
      return;
    }
    if (!protocolStepsPayload.length) {
      setAnalyzeError(t('sac_modal_err_noSteps'));
      return;
    }

    setLoadingAnalyze(true);
    setAnalyzeError('');
    try {
      const selectedRows = rows.filter((row) =>
        selectedIds.includes(row.__rowKey)
      );
      const transcriptIds = selectedRows
        .map((row) => row.id)
        .filter((id) => id !== null && id !== undefined);

      if (!transcriptIds.length) {
        setAnalyzeError(t('sac_modal_err_noIds'));
        return;
      }

      await onAnalyze?.({
        transcriptIds,
        steps: protocolStepsPayload,
        excludeOmittedAudio: true,
        protocol: {
          id: selectedProtocol.value,
          label: selectedProtocol.label,
          businessLineName: selectedProtocol.businessLineName || '',
        },
        filters: {
          dateFrom: filters.dateFrom || '',
          dateTo: filters.dateTo || '',
          feeling: filters.feeling || '',
          maxRows: clampRows(filters.maxRows),
        },
      });

      onClose?.();
    } catch (error) {
      setAnalyzeError(
        error?.customMessage || error?.message || t('sac_modal_err_analyze')
      );
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const columns = [
    {
      label: (
        <input
          ref={headerCheckboxRef}
          type='checkbox'
          aria-label={t('sac_modal_selectAll')}
          checked={allSelected}
          onChange={toggleAll}
          disabled={rowKeys.length === 0}
        />
      ),
      width: '48px',
      render: (row) => (
        <input
          type='checkbox'
          aria-label={t('sac_modal_selectRow', { id: row.id ?? row.__rowKey })}
          checked={selectedIds.includes(row.__rowKey)}
          onChange={() => toggleRow(row.__rowKey)}
        />
      ),
    },
    { accessor: 'id', label: t('sac_modal_col_id'), render: (row) => row.id ?? '--' },
    {
      accessor: 'audioDate',
      label: t('sac_modal_col_audioDate'),
      render: (row) => toDisplayDate(row.audioDate),
    },
    {
      accessor: 'feeling',
      label: t('sac_modal_col_feeling'),
      render: (row) => row.feeling || '--',
    },
    {
      accessor: 'summary',
      label: t('sac_modal_col_summary'),
      render: (row) => (
        <span className='marketing-sac__truncate' title={row.summary || '--'}>
          {row.summary || '--'}
        </span>
      ),
    },
    {
      accessor: 'createdAt',
      label: t('sac_modal_col_created'),
      render: (row) => toDisplayDateTime(row.createdAt),
    },
  ];

  if (!Modal) return null;

  return (
    <Modal
      title={t('sac_modal_title')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={loadingAnalyze ? undefined : t('sac_modal_analyze')}
      primaryBtnAction={loadingAnalyze ? undefined : handleAnalyze}
      disabledPrimaryBtn={
        loadingAnalyze
          ? true
          : !selectedIds.length ||
            !selectedProtocol?.value ||
            !protocolStepsPayload.length ||
            loadingSearch ||
            loadingProtocolSteps ||
            !!dateRangeError
      }
      secondaryBtnTxt={loadingAnalyze ? undefined : t('sac_modal_close')}
      secondaryBtnAction={loadingAnalyze ? undefined : onClose}
      ctaAligment='right'
      width='88vw'
    >
      <div className='marketing-sac-filter-modal'>
        {loadingAnalyze ? (
          <div
            key='marketing-sac-loading'
            className='marketing-sac__loading marketing-sac__fade-view'
          >
            <div
              ref={analyzeLoaderRef}
              className='marketing-sac__spinner'
              aria-hidden='true'
            />
            {!analyzeHasLottie && (
              <div
                className='spinner-border text-primary'
                role='status'
                aria-hidden='true'
              />
            )}
            <p className='mb-0'>{t('sac_modal_analyzing')}</p>
          </div>
        ) : (
          <div
            key='marketing-sac-form'
            className='marketing-sac__form marketing-sac__fade-view'
          >
            <div className='row g-3 align-items-end mb-1'>
              <div className='col-12 col-md-6'>
                {ComboBox ? (
                  <ComboBox
                    id='sac-protocol'
                    label={t('sac_modal_protocolLabel')}
                    options={protocolOptions}
                    value={selectedProtocol}
                    onChange={handleProtocolChange}
                    placeholder={t('sac_modal_protocolPlaceholder')}
                    disabled={loadingProtocols}
                  />
                ) : (
                  <>
                    <label className='form-label'>{t('sac_modal_protocolLabel')}</label>
                    <select
                      className='form-select'
                      value={selectedProtocol?.value ?? ''}
                      onChange={(event) => {
                        const next = protocolOptions.find(
                          (option) =>
                            String(option.value) === String(event.target.value)
                        );
                        handleProtocolChange(next || null);
                      }}
                      disabled={loadingProtocols}
                    >
                      <option value=''>{t('sac_modal_protocolPlaceholder')}</option>
                      {protocolOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div className='col-12 col-md-6'>
                <div className='caption'>
                  {loadingProtocols && t('sac_modal_loadingProtocols')}
                  {!loadingProtocols && selectedProtocol?.businessLineName && (
                    <>{t('sac_modal_businessLine', { name: selectedProtocol.businessLineName })}</>
                  )}
                  {!loadingProtocols && selectedProtocol && (
                    <>
                      {' '}
                      | {loadingProtocolSteps
                        ? t('sac_modal_stepsLoading')
                        : t('sac_modal_stepsLabel', { count: protocolSteps.length })}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className='row g-3 align-items-end'>
              <div className='col-12 col-md-3'>
                {Input ? (
                  <Input
                    label={t('sac_modal_dateFrom')}
                    type='date'
                    value={filters.dateFrom}
                    onChange={handleChange('dateFrom')}
                  />
                ) : (
                  <>
                    <label className='form-label'>{t('sac_modal_dateFrom')}</label>
                    <input
                      type='date'
                      className='form-control'
                      value={filters.dateFrom}
                      onChange={handleChange('dateFrom')}
                    />
                  </>
                )}
              </div>
              <div className='col-12 col-md-3'>
                {Input ? (
                  <Input
                    label={t('sac_modal_dateTo')}
                    type='date'
                    value={filters.dateTo}
                    onChange={handleChange('dateTo')}
                  />
                ) : (
                  <>
                    <label className='form-label'>{t('sac_modal_dateTo')}</label>
                    <input
                      type='date'
                      className='form-control'
                      value={filters.dateTo}
                      onChange={handleChange('dateTo')}
                    />
                  </>
                )}
              </div>
              <div className='col-12 col-md-2'>
                {ComboBox ? (
                  <div>
                    <ComboBox
                      id='sac-feeling'
                      label={t('sac_modal_feelingLabel')}
                      options={feelingOptions}
                      value={getFeelingOption(filters.feeling)}
                      onChange={handleFeelingChange}
                      placeholder={t('sac_modal_feelingLabel')}
                    />
                    <div className='helper-ghost'></div>
                  </div>
                ) : (
                  <>
                    <label className='form-label'>{t('sac_modal_feelingLabel')}</label>
                    <select
                      className='form-select'
                      value={filters.feeling}
                      onChange={handleChange('feeling')}
                    >
                      {feelingOptions.map((option) => (
                        <option
                          key={option.value || 'all'}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div className='col-12 col-md-2'>
                {Input ? (
                  <Input
                    label={t('sac_modal_limitLabel')}
                    type='number'
                    min={1}
                    max={500}
                    value={filters.maxRows}
                    onChange={handleChange('maxRows')}
                  />
                ) : (
                  <>
                    <label className='form-label'>{t('sac_modal_limitLabel')}</label>
                    <input
                      type='number'
                      className='form-control'
                      min={1}
                      max={500}
                      value={filters.maxRows}
                      onChange={handleChange('maxRows')}
                    />
                  </>
                )}
              </div>
              <div className='col-12 col-md-2 d-flex justify-content-end'>
                {Button ? (
                  <div>
                    <Button
                      label={loadingSearch ? t('sac_modal_searching') : t('sac_modal_search')}
                      variant='secondary'
                      size='sm'
                      onClick={handleSearch}
                      disabled={loadingSearch || !!dateRangeError}
                    />
                    <div className='helper-ghost'></div>
                  </div>
                ) : (
                  <button
                    type='button'
                    className='btn btn-secondary btn-sm'
                    onClick={handleSearch}
                    disabled={loadingSearch || !!dateRangeError}
                  >
                    {loadingSearch ? t('sac_modal_searching') : t('sac_modal_search')}
                  </button>
                )}
              </div>
            </div>

            {protocolError && (
              <p className='caption text-danger mt-2 mb-0'>{protocolError}</p>
            )}
            {dateRangeError && (
              <p className='caption text-danger mt-2 mb-0'>{dateRangeError}</p>
            )}
            {searchError && (
              <p className='caption text-danger mt-2 mb-0'>{searchError}</p>
            )}
            {analyzeError && (
              <p className='caption text-danger mt-2 mb-0'>{analyzeError}</p>
            )}

            <div className='marketing-sac__meta d-flex gap-3 mt-3 mb-2'>
              <span className='caption'>
                {t('sac_modal_rowsReceived', { count: rowCount })}
              </span>
              <span className='caption'>
                {t('sac_modal_selected', { count: selectedIds.length })}
              </span>
              <span className='caption'>
                {t('sac_modal_protocolSteps', { count: protocolStepsPayload.length })}
              </span>
            </div>

            {Table ? (
              <div className='table-container marketing-sac__table'>
                <Table columns={columns} data={rows} loading={loadingSearch} />
              </div>
            ) : (
              <p className='caption font-light mb-0'>
                {t('sac_modal_tableUnavailable')}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SacTranscriptsFilterModal;
